import * as React from "react";
import BottomDrawer from ".";
import Button from "../button";
import { vw } from "../../../lib/utils/responsiveUtils";
import { holeInCourseOf } from "../../../lib/utils/score.utils";
import type { DrawerCore } from "../../../pages/score/detail";

type Props = {
  open: boolean;
  onClose: () => void;
  data: DrawerCore;
  courseName?: string;
  onSubmit: (score: number) => void;
  onMoveHole: (step: 1 | -1) => void;
};

const presets = (p: number) => [
  { label: "이글", value: p - 2, diff: -2 },
  { label: "버디", value: p - 1, diff: -1 },
  { label: "파", value: p, diff: 0 },
  { label: "보기", value: p + 1, diff: +1 },
  { label: "더블 보기", value: p + 2, diff: +2 },
];

export default function ScorePersonalDrawer({
  open,
  onClose,
  data,
  courseName,
  onSubmit,
  onMoveHole,
}: Props) {
  const { holeNo, par, isEdit, score } = data;

  const [scoreValue, setScoreValue] = React.useState<number>(NaN);

  React.useEffect(() => {
    if (!open) return;

    if (typeof score === "number" && !Number.isNaN(score)) {
      setScoreValue(score); // 기존 점수 있으면 그걸로
    } else {
      setScoreValue(par); // 없으면 무조건 par로 시작
    }
  }, [open, par, score]);

  return (
    <BottomDrawer
      open={open}
      onClose={onClose}
      maxHeightRatio={1}
      className="rounded-t-[20px] overflow-hidden"
    >
      <div className="bg-white pb-[88px]">
        {/* 상단: 홀 정보 */}
        <div className="mt-[24px] mb-[18px] flex items-center justify-between gap-2">
          <button
            type="button"
            className="flex shrink-0 items-center justify-center"
            onClick={() => onMoveHole(-1)}
          >
            <img
              src="/icons/ic-back.webp"
              alt="이전 홀"
              style={{ width: vw(20), height: vw(20) }}
            />
          </button>

          <div className="min-w-0 flex-1 text-center font-[500] text-[1.375rem] leading-snug break-keep">
            <span>{courseName} </span>
            <span className="text-[#222]">{holeInCourseOf(holeNo)}홀</span>
            <span className="text-[#888]">(파{par})</span>
          </div>

          <button
            type="button"
            className="flex shrink-0 items-center justify-center"
            onClick={() => onMoveHole(1)}
          >
            <img
              src="/icons/ic-right.webp"
              alt="다음 홀"
              style={{ width: vw(20), height: vw(20) }}
            />
          </button>
        </div>

        {/* 프리셋 + 스텝퍼 */}
        <div className="mt-[10px] space-y-3">
          <div className="mb-[35px] flex gap-[13px]">
            <div className="bg-[#f6f6f6] rounded-[15px] p-[19px] flex-1 min-w-0">
              {presets(par).map((opt, i) => {
                const selected = scoreValue === opt.value;
                const diffText =
                  opt.diff === 0
                    ? ""
                    : `${opt.diff > 0 ? `(+${opt.diff})` : `(${opt.diff})`}`;
                return (
                  <div
                    key={i}
                    className="mb-[10px] flex items-center gap-[8px] font-[500] leading-snug text-[1.25rem] last:mb-0"
                  >
                    <span className="min-w-0 flex-1 break-keep text-right text-[#222]">
                      {opt.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => setScoreValue(opt.value)}
                      className={`w-[min(6.25rem,30vw)] min-h-[3.125rem] shrink-0 rounded-[10px] border px-2 text-center font-bold leading-none text-[1.25rem] transition
                        ${selected ? "bg-white shadow-[0_6px_12px_rgba(0,0,0,0.15)] border-[#222]" : "bg-white border-[#e6e6e6]"}`}
                    >
                      {opt.value}
                    </button>
                    <span
                      className={`min-w-0 flex-1 break-keep text-left font-[500] ${
                        opt.diff > 0
                          ? "text-[#155be9]"
                          : opt.diff < 0
                            ? "text-[#e5002b]"
                            : "text-transparent"
                      }`}
                    >
                      {diffText || "\u00a0"}
                    </span>
                  </div>
                );
              })}

              {/* 스텝퍼 */}
              <div className="mt-[15px]">
                <div className="mx-auto flex w-full max-w-[245px] items-center justify-between gap-2 rounded-full bg-white px-4 py-2">
                  <button
                    type="button"
                    className="w-fit h-full flex items-center justify-center"
                    onClick={() => setScoreValue((s) => (s > 1 ? s - 1 : par))}
                    aria-label="감소"
                  >
                    <img
                      className="w-6"
                      src="/icons/ic-minus.webp"
                      alt="minus"
                    />
                  </button>

                  <span className="min-w-0 text-center font-bold leading-none text-[#222] select-none text-[1.75rem]">
                    {Number.isFinite(scoreValue) ? scoreValue : par}
                  </span>

                  <button
                    type="button"
                    className="w-fit h-full flex items-center justify-center"
                    onClick={() =>
                      setScoreValue((s) =>
                        Number.isFinite(s) ? s + 1 : par + 1,
                      )
                    }
                    aria-label="증가"
                  >
                    <img
                      className="size-6"
                      src="/icons/ic_plus.png"
                      alt="plus"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="fixed bottom-0 left-0 right-0 z-50 mx-auto w-full max-w-[720px] bg-white px-[15px] py-[10px]">
          <Button
            variant={isEdit ? "black" : "red"}
            className="h-auto min-h-[50px] w-full whitespace-normal px-4 py-3 font-bold"
            onClick={() => {
              const finalScore = Number.isFinite(scoreValue) ? scoreValue : par;
              onSubmit(finalScore);
            }}
          >
            <span className="min-w-0 break-keep text-center leading-snug">
              {courseName} {holeInCourseOf(holeNo)}홀 {isEdit ? "수정" : "입력"}{" "}
              완료
            </span>
          </Button>
        </div>
      </div>
    </BottomDrawer>
  );
}
